import Vue from 'vue';
import {
  channelsService,
  dashboardService,
  labelsService,
  ordersService,
  placementsService,
  projectsService,
  statisticsService
} from 'services';

import widgetTypesGlobal from 'store/modules/dashboards/widgets/global/widgetTypes';
import widgetTypesProject from 'store/modules/dashboards/widgets/project/widgetTypes';
import widgetPrototypesGlobal from 'store/modules/dashboards/widgets/global/widgetPrototypes';
import widgetPrototypesProject from 'store/modules/dashboards/widgets/project/widgetPrototypes';

import compileWidget from 'store/modules/dashboards/widgets/compileWidget/compileWidget';
import getDashboardData from 'store/modules/dashboards/dataGetter/getDashboardData';
import filterWidgetsWithNonExistingMetrics from 'store/modules/dashboards/utils/filterWidgetsWithNonExistingMetrics';
import {showNotification} from 'components/common/MNotification/MNotificationManager';

import translation from 'translation';
import moment from 'moment-wrapper';
import tracker from 'tracking';

import keyBy from 'lodash.keyby';
import _ from 'lodash';
import ApiBadRequestError from 'utils/api/errors/ApiBadRequestError';

_.noConflict();

const dashboardTypesVerbose = {
  CURRENT_USER: {
    name: () => translation.trc('DASHBOARD', 'Private'),
    hint: () => translation.tr('Dashboard is only visible for you')
  },
  MANAGER: {
    name: () => translation.trc('DASHBOARD', 'Internal'),
    hint: () => translation.tr('Visible to users who have access to dashboard projects, not available to clients')
  },
  CLIENT: {
    name: () => translation.trc('DASHBOARD', 'Open'),
    hint: () => translation.tr('Visible to all users with access to dashboard projects')
  }
};

function getDefaultDashboardParams () {
  return {
    name: translation.tr('My dashboard'),
    widgets: [],
    accounts: [],
    access_type: 'CURRENT_USER'
  };
}

function getEmptyState () {
  return {
    availableChannelIds: [],
    availableMetrics: [],
    availablePlanMetrics: null,

    availableOrders: [],
    availablePlacements: [],
    availableLabels: [],
    availableProfiles: [],
    availableProjects: [],

    dashboard: null,
    dashboardDateStart: moment().subtract(5, 'days').
      toDate(),
    dashboardDateEnd: moment().toDate(),

    dashboardAccountProjectIdsMap: {},

    widgetData: {},
    widgetLoadingState: {}, // is loading etc
    dashboardAccessTypes: []
  };
}

const dashboardsStore = {
  namespaced: true,
  state: getEmptyState(),
  getters: {
    isGlobalDashboard (state, getters, rootState, rootGetters) {
      return !rootGetters['projects/currentProjectId'];
    },

    dashboardId (state, getters) {
      if (getters.isGlobalDashboard) {
        return state.dashboard ? state.dashboard.id : null;
      }

      return null;
    },
    dashboardAccessTypes (state) {
      return state.dashboardAccessTypes.map(item => {
        let name = item.name;
        let hint = '';

        const verbose = dashboardTypesVerbose[item.id];
        if (verbose) {
          name = verbose.name();
          hint = verbose.hint();
        }
        return {
          ...item,
          name,
          hint
        };
      });
    },
    isUserDashboard (state, getters, rootState, rootGetters) {
      const currentUser = rootGetters['user/currentUser'];
      return state.dashboard && state.dashboard.user_id === currentUser.id || !getters.isGlobalDashboard;
    },
    userCanEditDashboard (state, getters) {
      return getters.isUserDashboard;
    },
    dashboardMetrics: state => keyBy(state.availableMetrics, 'id'),
    dashboardAvailableMetrics (state) {
      const availableChannelsSet = new Set(state.availableChannelIds);
      const filteredList = state.availableMetrics.filter(metric => {
        if (metric.channel_ids) {

          // skip metric that hasn't got channel from available channels
          if (!metric.channel_ids.some(cId => availableChannelsSet.has(cId))){
            return false;
          }
        }
        return true;
      });

      return keyBy(filteredList, 'id');
    },
    dashboardPlanMetrics: state => keyBy(state.availablePlanMetrics, 'id'),
    dashboardAccessType: state => state.dashboard ? state.dashboard.access_type : 'CURRENT_USER',

    dashboardAvailableProjects: state => state.availableProjects,
    dashboardAvailableProfiles: state => state.availableProfiles,
    dashboardAvailableOrders: state => state.availableOrders,
    dashboardAvailableLabels: state => state.availableLabels,
    dashboardAvailablePlacements: state => state.availablePlacements,
    dashboardAvailableChannels: (state, getters, rootState, rootGetters) => rootGetters['channels/projectDetailedStatisticsChannels'],
    dashboardAvailableGoals (state, getters) {
      return getters.dashboardAvailableProfiles.map(i => i.children).flat();
    },


    currentDashboard: state => state.dashboard,

    dashboardName: state => state.dashboard ? state.dashboard.name : '',
    dashboardUserEmail: state => state.dashboard ? state.dashboard.user_email : '',

    dashboardDateStart: state => state.dashboardDateStart,
    dashboardDateEnd: state => state.dashboardDateEnd,

    dashboardAccountsFiltration: state => state.dashboard ? state.dashboard.accounts : [],
    dashboardActiveAccountIds (state, getters) {
      return getters.dashboardAccountsFiltration.map(i => i.account_id);
    },

    dashboardCurrencyIds (state, getters, rootState, rootGetters) {
      const currencyIdsSet = new Set();

      const accountIds = getters.dashboardActiveAccountIds;

      accountIds.forEach(accId => {
        const account = rootGetters['sidebar/getAccountById'](accId);
        if (!account || !account.currency_id) {
          return;
        }
        currencyIdsSet.add(account.currency_id);
      });

      return Array.from(currencyIdsSet);
    },
    dashboardCurrencyId (state, getters) {
      const currencies = getters.dashboardCurrencyIds;
      if (currencies.length !== 1){
        return null;
      }
      return currencies[0];
    },
    widgetsTypeMap (state, getters) {
      if (getters.isGlobalDashboard) {
        return keyBy(widgetTypesGlobal(), 'id');
      }

      return keyBy(widgetTypesProject(), 'id');
    },
    widgetsPrototypesMap (state, getters) {
      if (getters.isGlobalDashboard) {
        return keyBy(widgetPrototypesGlobal, 'type');
      }

      return keyBy(widgetPrototypesProject, 'type');
    },

    dashboardProjectsFiltration (state, getters, rootState, rootGetters) {
      if (rootGetters['projects/currentProjectId']) {
        return [rootGetters['projects/currentProjectId']];
      }

      if (!getters.dashboardAccountsFiltration.some(i => i.project_ids.length > 0)) {
        return false;
      }

      return Object.keys(state.dashboardAccountProjectIdsMap).map(_accountId => {
        const accountId = parseInt(_accountId);
        const projectIds = state.dashboardAccountProjectIdsMap[accountId];
        const accountParams = getters.dashboardAccountsFiltration.find(i => i.account_id === accountId);
        if (!accountParams) {
          return [];
        }
        const filteredProjects = accountParams.project_ids;
        if (filteredProjects && filteredProjects.length > 0) {
          return filteredProjects;
        }
        return projectIds;
      }).
        flat();
    },

    dashboardParams: (state, getters) => ({
      dateStart: moment(state.dashboardDateStart).formatDateApi(),
      dateEnd: moment(state.dashboardDateEnd).formatDateApi(),
      projectsFiltration: getters.dashboardProjectsFiltration,
      accountsFiltration: getters.dashboardActiveAccountIds
    }),

    dashboardWidgets (state, getters) {
      if (!state.dashboard) {
        return [];
      }
      const enabledWidgetTypes = new Set(Object.keys(getters.widgetsTypeMap));

      const widgets = state.dashboard.widgets.filter(w => enabledWidgetTypes.has(w.type)).map(
        widget => compileWidget(widget, getters.dashboardParams, getters.widgetsTypeMap)
      );

      return filterWidgetsWithNonExistingMetrics(widgets, getters.dashboardMetrics);
    },

    getWidgetById (state, getters) {
      return (widgetId) => getters.dashboardWidgets.find(i => i.id === widgetId);
    },

    selectedProjectsInDashboardFilter (state, getters) {
      return getters.dashboardAvailableProjects.filter(item => {
        if (getters.dashboardProjectsFiltration === false && item.status === 'ACTIVE') {
          return true;
        } else if (getters.dashboardProjectsFiltration &&
            getters.dashboardProjectsFiltration.some(id => id === item.id)) {
          return true;
        }

        return false;
      });
    },

    getWidgetIsLoading: state => (widgetId) => state.widgetLoadingState[widgetId] || false,

    getDataForWidget: state => (widgetId) => {
      const widgetData = state.widgetData[widgetId];
      if (widgetData) {
        return widgetData;
      }

      return false;
    },
    getDataForWidgetOrDefault: (state, getters) => (widgetId) => {
      const widgetData = getters.getDataForWidget(widgetId);
      if (widgetData) {
        return widgetData;
      }

      return {
        data: {
          items: [],
          total: {}
        },
        lastPeriodData: {
          items: [],
          total: {}
        }
      };
    }
  },
  mutations: {
    RESET_DASHBOARD_STATE (state) {
      const newState = getEmptyState();
      Vue.set(state, 'dashboard', newState.dashboard);
      Vue.set(state, 'widgetData', newState.widgetData);
    },
    SET_DASHBOARD (state, dashboard) {
      state.dashboard = dashboard;
    },
    SET_DASHBOARD_ACCESS_TYPES (state, types) {
      state.dashboardAccessTypes = types;
    },
    SET_AVAILABLE_CHANNEL_IDS (state, channelIds) {
      state.availableChannelIds = channelIds;
    },
    SET_DASHBOARD_METRICS (state, metrics) {
      state.availableMetrics = metrics;
    },
    SET_DASHBOARD_PLAN_METRICS (state, metrics) {
      state.availablePlanMetrics = metrics;
    },
    SET_DASHBOARD_WEB_ANALYTICS (state, profiles) {
      state.availableProfiles = profiles;
    },

    SET_AVAILABLE_LABELS (state, labels) {
      state.availableLabels = labels;
    },
    SET_AVAILABLE_PROJECTS (state, projects) {
      state.availableProjects = projects;
    },
    SET_AVAILABLE_PLACEMENTS (state, placements) {
      state.availablePlacements = placements;
    },
    SET_AVAILABLE_ORDERS (state, orders) {
      state.availableOrders = orders;
    },
    SET_DASHBOARD_DATE_RANGE (state, {dateStart, dateEnd}) {
      state.dashboardDateStart = dateStart;
      state.dashboardDateEnd = dateEnd;
    },
    SET_WIDGET_LOADING_STATE (state, {widgetId, isLoading}) {
      Vue.set(state.widgetLoadingState, widgetId, isLoading);
    },
    SET_WIDGET_DATA (state, {widgetId, data}) {
      Vue.set(state.widgetData, widgetId, data);
    },
    SET_WIDGETS_LAYOUT (state, layoutParams) {
      const widgetParamsMap = keyBy(layoutParams, 'id');
      state.dashboard.widgets.forEach(widget => {
        const params = widgetParamsMap[widget.id];

        // skip update if widget is hidden (f.e. metric not present or component disabled)
        if (!params) {
          return;
        }
        widget.layoutParams.x = params.x;
        widget.layoutParams.y = params.y;
        widget.layoutParams.w = params.w;
        widget.layoutParams.h = params.h;
      });
    },
    ADD_WIDGET_TO_DASHBOARD (state, newWidget) {
      state.dashboard.widgets.push(newWidget);
    },
    UPDATE_DASHBOARD_WIDGET (state, {id, data}) {
      const widgetIndex = state.dashboard.widgets.findIndex(i => i.id === id);
      Vue.set(state.dashboard.widgets, widgetIndex, data);
      return state.dashboard.widgets[widgetIndex];
    },
    DELETE_DASHBOARD_WIDGET (state, widgetId) {
      state.dashboard.widgets = state.dashboard.widgets.filter(i => i.id !== widgetId);
    },
    SET_DASHBOARD_ACCOUNTS_FILTRATION (state, filtration) {
      state.dashboard.accounts = filtration;
    },
    SET_DASHBOARD_ACCESS_TYPE (state, accessType) {
      state.dashboard.access_type = accessType;
    },
    SET_DASHBOARD_NAME (state, name) {
      state.dashboard.name = name;
    },

    SET_DASHBOARD_ACCOUNT_PROJECTS_MAP (state, projectIds) {
      state.dashboardAccountProjectIdsMap = projectIds;
    }
  },
  actions: {
    RESET_DASHBOARD_STATE (context) {
      context.commit('RESET_DASHBOARD_STATE');
    },
    async FETCH_DASHBOARD_ACCESS_TYPES (context) {
      if (context.getters.isGlobalDashboard) {
        const types = await dashboardService.getDashboardAccessTypes();
        context.commit('SET_DASHBOARD_ACCESS_TYPES', types);
      }
    },
    async FETCH_DASHBOARD_DATA (context, ignoreCache = false) {
      await context.dispatch('FETCH_DATA_FOR_WIDGETS', {
        widgets: context.getters.dashboardWidgets,
        ignoreCache
      });
    },
    async FETCH_DATA_FOR_WIDGETS (context, {widgets, ignoreCache = false}) {
      if (context.getters.dashboardActiveAccountIds.length === 0) {
        return;
      }
      const promisesDict = await getDashboardData(
        context.getters.currentDashboard,
        widgets,
        context.getters.dashboardParams,
        context.getters.dashboardAvailableProfiles,
        context.getters.dashboardAvailableGoals,
        context.rootGetters['common/vatOn'],
        context.getters.dashboardMetrics,
        ignoreCache
      );
      const promisesList = Object.keys(promisesDict).map(async widgetId => {
        context.commit('SET_WIDGET_LOADING_STATE', {widgetId, isLoading: true});
        const data = await promisesDict[widgetId];
        context.commit('SET_WIDGET_DATA', {widgetId, data});
        context.commit('SET_WIDGET_LOADING_STATE', {widgetId, isLoading: false});
      });

      await Promise.all(promisesList);

      if (context.getters.isGlobalDashboard) {
        tracker.track('dashboard_is_opened', {dashboardId: context.getters.dashboardId});
      } else if (context.state.dashboard) {
        tracker.track('project_info_is_opened', {dashboardId: context.state.dashboard.accounts[0].project_ids[0]});
      }
    },

    async FETCH_DASHBOARD_PROJECTS (context) {
      const accounts = context.getters.dashboardAccountsFiltration;
      const accountProjectMap = {};

      const accountIds = accounts.map(a => a.account_id);
      const detailedMap = await context.dispatch('sidebar/FETCH_ACCOUNTS_PROJECTS_BATCH', accountIds, {root: true});

      Object.keys(detailedMap).forEach(accountId => {
        accountProjectMap[accountId] = detailedMap[accountId].map(p => p.id);
      });

      context.commit('SET_DASHBOARD_ACCOUNT_PROJECTS_MAP', accountProjectMap);
    },

    async CREATE_NEW_DASHBOARD (context, {name, accessType, accounts, widgets}) {
      const dashboardParams = getDefaultDashboardParams();
      dashboardParams.name = name;
      dashboardParams.access_type = accessType;
      dashboardParams.accounts = accounts;
      dashboardParams.widgets = widgets;

      const dashboard = await dashboardService.createDashboard(dashboardParams);
      context.dispatch('sidebar/FETCH_AVAILABLE_DASHBOARDS', {}, {root: true});
      return dashboard;
    },
    async DELETE_DASHBOARD (context) {
      const dashboardId = context.getters.dashboardId;
      await dashboardService.deleteDashboard(dashboardId);
      context.dispatch('sidebar/FETCH_AVAILABLE_DASHBOARDS', {}, {root: true});
    },
    async FETCH_DASHBOARD (context, {dashboardId, accountId, projectId}) {
      let dashboard;
      if (context.getters.isGlobalDashboard) {
        dashboard = await dashboardService.getDashboard(dashboardId);
      } else {
        dashboard = await dashboardService.getProjectDashboard(accountId, projectId);
        dashboard.accounts = [{account_id: accountId, project_ids: [projectId]}];
      }
      context.commit('SET_DASHBOARD', dashboard);
    },
    async SAVE_DASHBOARD_STATE (context) {
      const isGlobalDashboard = context.getters.isGlobalDashboard;
      const dashboard = context.state.dashboard;
      dashboard.isGlobalDashboard = isGlobalDashboard;
      let dashboardId;

      if (isGlobalDashboard) {
        dashboardId = dashboard.id;
      } else {
        dashboardId = dashboard.accounts[0].project_ids[0];
      }

      try {
        await dashboardService.updateDashboard(dashboardId, dashboard);
      } catch (error) {
        if (error instanceof ApiBadRequestError) {
          const errorMessage = error.getErrorMessage();

          let message = translation.tr('Failed to update the dashboard because ');
          message += errorMessage[0].toLowerCase() + errorMessage.substring(1);

          showNotification({
            type: 'error',
            header: errorMessage,
            text: message
          });

          return;
        }

        throw error;
      }
    },
    async SET_DASHBOARD_DATE_RANGE (context, {dateStart, dateEnd, triggerUpdate = true}) {
      context.commit('SET_DASHBOARD_DATE_RANGE', {dateStart, dateEnd});
      if (triggerUpdate) {
        context.dispatch('FETCH_DASHBOARD_DATA');
      }
    },
    async FETCH_DASHBOARD_METRICS (context) {
      const profileIds = context.getters.dashboardAvailableProfiles.map(p => p.id);
      let metrics = [];
      if (!context.getters.isGlobalDashboard) {
        metrics = await statisticsService.getProjectDetailedMetricsList(
          context.rootGetters['accounts/currentAccountId'],
          context.rootGetters['projects/currentProjectId'],
          profileIds
        );
      } else if (context.getters.dashboardActiveAccountIds.length > 0) {
        metrics = await statisticsService.getDetailedStatisticsMetricsList(
          {accountIds: context.getters.dashboardActiveAccountIds, profiles: profileIds}
        );
      } else {
        metrics = statisticsService.getDefaultDetailedStatisticsMetrics();
      }
      const metricsFilter = metrics.filter(metric => !(metric?.is_organic ?? false));
      context.commit('SET_DASHBOARD_METRICS', metricsFilter);
    },
    async FETCH_DASHBOARD_AVAILABLE_CHANNELS (context) {
      const accounts = context.getters.isGlobalDashboard
        ? context.getters.dashboardActiveAccountIds : [context.rootGetters['accounts/currentAccountId']];

      let channels = [];
      if (accounts.length > 0) {
        channels = await channelsService.getAccountChannels(accounts);
      }

      context.commit('SET_AVAILABLE_CHANNEL_IDS', channels.map(c => c.id));
    },
    async FETCH_DASHBOARD_PLAN_METRICS (context) {
      let metrics = [];
      if (context.getters.dashboardId) {
        metrics = await dashboardService.getDashboardPlanMetrics(context.getters.dashboardId);
      } else if (context.state.dashboard && context.state.dashboard.accounts) {
        metrics = await dashboardService.getProjectPlanMetricsList(context.state.dashboard.accounts[0].project_ids[0]);
      }
      context.commit('SET_DASHBOARD_PLAN_METRICS', metrics);
    },
    async FETCH_AVAILABLE_LABELS (context) {
      let labels = [];
      const accountIds = context.getters.dashboardActiveAccountIds;
      if (accountIds.length > 0) {
        labels = await labelsService.getAccountsLabels(
          accountIds
        );
      }

      context.commit('SET_AVAILABLE_LABELS', labels);
    },
    async FETCH_AVAILABLE_PROJECTS (context) {
      let projects = [];
      if (context.getters.dashboardActiveAccountIds.length > 0) {
        projects = (
          await projectsService.getAccountProjectsListSimple(context.getters.dashboardActiveAccountIds)
        ).items;
      }
      context.commit('SET_AVAILABLE_PROJECTS', projects);
    },
    async FETCH_DASHBOARD_AVAILABLE_PLACEMENTS (context) {
      let placements = [];
      if (context.getters.dashboardActiveAccountIds.length > 0) {
        placements = await placementsService.getAccountPlacements(context.getters.dashboardActiveAccountIds);
      }
      context.commit('SET_AVAILABLE_PLACEMENTS', placements);
    },
    async FETCH_DASHBOARD_WEB_ANALYTICS (context) {
      let profiles = [];
      const accountIds = context.getters.dashboardActiveAccountIds;
      if (accountIds.length > 0) {
        try {
          /*
           * use separate method for project level dashboards. Because profiles are grouped by xid
           * and current project's profile can be grouped with other project's profile,
           * resulted profile may have other project id in `project_id` field.
           * Later in getter this profile will be filtered out (because we show profiles only for current project)
           */
          if (context.rootGetters['projects/currentProjectId']) {
            await Promise.all([
              context.dispatch('webanalytics/FETCH_PROJECT_PROFILES', {
                accountId: accountIds[0], projectId: context.rootGetters['projects/currentProjectId']
              }, {root: true}),
              context.dispatch('webanalytics/FETCH_PROJECT_GOALS', {
                accountId: accountIds[0], projectId: context.rootGetters['projects/currentProjectId']
              }, {root: true})
            ]);
            profiles = context.rootGetters['webanalytics/projectProfilesAndGoals'];

            // manually add project_id (this is TMP hotfix) to make profile schema same as schema for account profiles
            profiles = profiles.map(p => ({
              ...p,
              project_id: context.rootGetters['projects/currentProjectId']
            }));
          } else {
            await Promise.all([
              context.dispatch('webanalytics/FETCH_ACCOUNT_PROFILES_MANY', {accountIds}, {root: true}),
              context.dispatch('webanalytics/FETCH_ACCOUNT_GOALS_MANY', {accountIds}, {root: true})
            ]);
            profiles = context.rootGetters['webanalytics/accountProfilesAndGoals'];
          }
        } catch (error) {
          if (error instanceof ApiBadRequestError) {
            console.warn(error.getErrorMessage());
          } else {
            throw error;
          }
        }
      }
      context.commit('SET_DASHBOARD_WEB_ANALYTICS', profiles);
    },
    async FETCH_DASHBOARD_AVAILABLE_ORDER (context) {
      let orders = [];
      const accountIds = context.getters.dashboardActiveAccountIds;
      if (accountIds.length > 0) {
        try {
          orders = await ordersService.getAccountsOrdersListSimple(
            accountIds
          );
        } catch (error) {
          if (error instanceof ApiBadRequestError) {
            console.warn(error.getErrorMessage());
          } else {
            throw error;
          }
        }
      }
      context.commit('SET_AVAILABLE_ORDERS', orders);
    },

    ADD_WIDGET_TO_DASHBOARD (context, widget) {
      context.commit('ADD_WIDGET_TO_DASHBOARD', widget);
      const compiledWidget = context.getters.getWidgetById(widget.id);
      context.dispatch('FETCH_DATA_FOR_WIDGETS', {widgets: [compiledWidget]});
      return context.dispatch('SAVE_DASHBOARD_STATE');
    },
    UPDATE_DASHBOARD_WIDGET (context, updateData) {
      context.commit('UPDATE_DASHBOARD_WIDGET', updateData);
      const compiledWidget = context.getters.getWidgetById(updateData.data.id);

      context.dispatch('FETCH_DATA_FOR_WIDGETS', {widgets: [compiledWidget]});
      context.dispatch('SAVE_DASHBOARD_STATE');
    },
    DELETE_DASHBOARD_WIDGET (context, widgetId) {
      context.commit('DELETE_DASHBOARD_WIDGET', widgetId);
      context.dispatch('SAVE_DASHBOARD_STATE');
    },
    SET_WIDGETS_LAYOUT (context, layoutParams) {
      context.commit('SET_WIDGETS_LAYOUT', layoutParams);
      return context.dispatch('SAVE_DASHBOARD_STATE');
    },
    SET_DASHBOARD_NAME_AND_ACCESS_TYPE (context, {name, accessType}) {
      context.commit('SET_DASHBOARD_ACCESS_TYPE', accessType);
      context.commit('SET_DASHBOARD_NAME', name);
      context.dispatch('SAVE_DASHBOARD_STATE');
      context.dispatch('sidebar/FETCH_AVAILABLE_DASHBOARDS', {}, {root: true});
    },
    SET_DASHBOARD_ACCESS_TYPE (context, accessType) {
      context.commit('SET_DASHBOARD_ACCESS_TYPE', accessType);
      return context.dispatch('SAVE_DASHBOARD_STATE');
    },
    async SET_DASHBOARD_ACCOUNTS_FILTRATION (context, filtration) {
      context.commit('SET_DASHBOARD_ACCOUNTS_FILTRATION', filtration);
      context.dispatch('SAVE_DASHBOARD_STATE');
      await context.dispatch('FETCH_DASHBOARD_PROJECTS');
      context.dispatch('FETCH_DASHBOARD_DATA');
    }
  }
};

export default dashboardsStore;
